/**
 * Platform Backup API Service
 *
 * API client for admin-only platform-level backup endpoints.
 * Calls /api/admin/backups/platform/* endpoints added in WQ-031 + WQ-033.
 *
 * Uses authFetch pattern consistent with backupService.ts.
 *
 * @module services/platformBackupService
 */

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

// =============================================================================
// Types (match backend schemas from app/schemas/backup.py + restore.py)
// =============================================================================

export interface PlatformBackupTriggerResponse {
  backup_id: string;
  status: string;
  message: string;
}

export interface PlatformBackupHistoryItem {
  id: string;
  status: string;
  backup_type: string;
  scope_snapshot: Record<string, unknown>;
  destination_type: string;
  destination_path: string | null;
  size_bytes: number | null;
  checksum: string | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  retention_tier?: 'daily' | 'weekly' | 'monthly' | 'on_demand';
}

export interface PlatformBackupHistoryResponse {
  items: PlatformBackupHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface PlatformBackupDetailResponse extends PlatformBackupHistoryItem {
  configuration_id: string | null;
  metadata: Record<string, unknown>;
}

export interface PlatformBackupConfigResponse {
  id: string;
  enabled: boolean;
  frequency: string;
  retention_count: number;
  retention_days: number;
  scope: Record<string, unknown>;
  destination_type: string;
  destination_config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PlatformBackupConfigUpdate {
  enabled?: boolean;
  frequency?: string;
  retention_count?: number;
  retention_days?: number;
  destination_type?: string;
  destination_config?: Record<string, unknown>;
}

export interface PlatformBackupToggleResponse {
  enabled: boolean;
  frequency: string;
  message: string;
}

export interface PlatformBackupNextRunResponse {
  enabled: boolean;
  frequency: string;
  last_backup_at: string | null;
  next_run_at: string | null;
  message: string;
}

export interface PlatformRestoreInitiatedResponse {
  restore_id: string;
  backup_id: string;
  status: string;
  message: string;
}

export interface PlatformRestoreDetailResponse {
  id: string;
  backup_id: string;
  pre_restore_backup_id: string | null;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
}

// =============================================================================
// API Functions
// =============================================================================

const PLATFORM_BACKUP_BASE = '/api/admin/backups/platform';

export const triggerPlatformBackup = async (
  authFetch: AuthFetch,
): Promise<PlatformBackupTriggerResponse> => {
  const response = await authFetch(PLATFORM_BACKUP_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to trigger platform backup');
  }
  return response.json();
};

export const getPlatformBackupHistory = async (
  authFetch: AuthFetch,
  limit = 20,
  offset = 0,
  filters?: { backup_type?: string; retention_tier?: string },
): Promise<PlatformBackupHistoryResponse> => {
  let url = `${PLATFORM_BACKUP_BASE}/history?limit=${limit}&offset=${offset}`;
  if (filters?.backup_type) url += `&backup_type=${encodeURIComponent(filters.backup_type)}`;
  if (filters?.retention_tier) url += `&retention_tier=${encodeURIComponent(filters.retention_tier)}`;
  const response = await authFetch(url);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch platform backup history');
  }
  return response.json();
};

export const getPlatformBackupDetail = async (
  authFetch: AuthFetch,
  backupId: string,
): Promise<PlatformBackupDetailResponse> => {
  const response = await authFetch(`${PLATFORM_BACKUP_BASE}/${backupId}`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch platform backup detail');
  }
  return response.json();
};

export const deletePlatformBackup = async (
  authFetch: AuthFetch,
  backupId: string,
): Promise<{ success: boolean; backup_id: string; message: string }> => {
  const response = await authFetch(`${PLATFORM_BACKUP_BASE}/${backupId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to delete platform backup');
  }
  return response.json();
};

// =============================================================================
// Config & Scheduling API Functions (WQ-033)
// =============================================================================

export const getPlatformBackupConfig = async (
  authFetch: AuthFetch,
): Promise<PlatformBackupConfigResponse | null> => {
  const response = await authFetch(`${PLATFORM_BACKUP_BASE}/config`);
  if (response.status === 404) return null;
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch platform backup config');
  }
  return response.json();
};

export const upsertPlatformBackupConfig = async (
  authFetch: AuthFetch,
  data: PlatformBackupConfigUpdate,
): Promise<PlatformBackupConfigResponse> => {
  const response = await authFetch(`${PLATFORM_BACKUP_BASE}/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to update platform backup config');
  }
  return response.json();
};

export const togglePlatformBackupSchedule = async (
  authFetch: AuthFetch,
  enabled: boolean,
): Promise<PlatformBackupToggleResponse> => {
  const response = await authFetch(`${PLATFORM_BACKUP_BASE}/config/toggle`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to toggle platform backup schedule');
  }
  return response.json();
};

export const getPlatformNextRun = async (
  authFetch: AuthFetch,
): Promise<PlatformBackupNextRunResponse> => {
  const response = await authFetch(`${PLATFORM_BACKUP_BASE}/next-run`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch next run info');
  }
  return response.json();
};

// =============================================================================
// Restore API Functions (WQ-033)
// =============================================================================

export const initiatePlatformRestore = async (
  authFetch: AuthFetch,
  backupId: string,
  skipVerification = false,
): Promise<PlatformRestoreInitiatedResponse> => {
  const response = await authFetch(`${PLATFORM_BACKUP_BASE}/${backupId}/restore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ skip_verification: skipVerification }),
  });
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to initiate platform restore');
  }
  return response.json();
};

export const getPlatformRestoreStatus = async (
  authFetch: AuthFetch,
  restoreId: string,
): Promise<PlatformRestoreDetailResponse> => {
  const response = await authFetch(`${PLATFORM_BACKUP_BASE}/restores/${restoreId}`);
  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.detail || 'Failed to fetch platform restore status');
  }
  return response.json();
};
